import { adminAxiosInstance } from "./adminAxiosInstance";

const FARMS_CACHE_TTL = 2 * 60 * 1000;
const farmsCache = new Map();
const farmRequests = new Map();

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
};

const normalizeFarm = (farm) => ({
  ...farm,
  id: String(farm.farm_id ?? farm.id ?? farm._id ?? ""),
  name: farm.farm_name || "Unnamed farm",
  slug: farm.slug || "Not available",
  ownerId: farm.owner_user_id || "Not available",
  ownerName: farm.owner_name || "Not available",
  ownerEmail: farm.owner_email || "Not available",
  ownerPhone: farm.owner_phone || "Not available",
  address: farm.address || "Not available",
  location: farm.location_string || farm.address || "Not available",
  products: Number(farm.total_products_listed || 0),
  verified: Boolean(farm.verified),
  active: Boolean(farm.active),
  rating: Number(farm.average_rating || 0),
  reviews: Number(farm.total_reviews || 0),
  createdAt: farm.created_at,
  createdDate: formatDate(farm.created_at),
});

const normalizeResponse = (response, params) => {
  const farms = Array.isArray(response?.data) ? response.data.map(normalizeFarm) : [];
  const pagination = response?.pagination || {};

  return {
    status: response?.status !== false,
    message: response?.message,
    segment: response?.segment ?? params.segment,
    period_days: response?.period_days ?? params.days,
    summary: {
      total_farms: Number(response?.summary?.total_farms ?? pagination.total_items ?? farms.length),
      verified_farms_count: Number(response?.summary?.verified_farms_count ?? 0),
    },
    pagination: {
      total_items: Number(pagination.total_items ?? farms.length),
      current_page: Number(pagination.current_page ?? params.page),
      total_pages: Number(pagination.total_pages ?? Math.max(1, Math.ceil(farms.length / params.pageSize))),
      pageSize: Number(pagination.pageSize ?? params.pageSize),
    },
    data: farms,
  };
};

export const getMarketingFarms = ({ segment = "all", days = 30, page = 1, pageSize = 10, sortBy = "name", order = "desc", search = "", forceRefresh = false } = {}) => {
  const params = { segment, days, page, pageSize, sortBy, order, ...(search?.trim?.() ? { search: search.trim() } : {}) };
  const cacheKey = JSON.stringify(params);
  const cached = farmsCache.get(cacheKey);
  if (!forceRefresh && cached && Date.now() - cached.updatedAt < FARMS_CACHE_TTL) return Promise.resolve(cached.data);
  if (farmRequests.has(cacheKey)) return farmRequests.get(cacheKey);

  const request = adminAxiosInstance
    .get("/admin/internal/marketing/farms", { params })
    .then(({ data }) => {
      const result = normalizeResponse(data, params);
      if (!result.status) throw new Error(result.message || "Farms could not be loaded.");
      farmsCache.set(cacheKey, { data: result, updatedAt: Date.now() });
      return result;
    })
    .finally(() => farmRequests.delete(cacheKey));

  farmRequests.set(cacheKey, request);
  return request;
};

export const clearMarketingFarmsCache = () => {
  farmsCache.clear();
  farmRequests.clear();
};
